/**
 * See database.ts. This represents elevated permission backend calls. These calls should only succeed
 * if the user is an admin (i.e. has a Nowadays account).
 */

import { escapeIlike } from "@/lib/utils/escape-ilike";
import { sendSlackMessage } from "@/components/ui/utils";
import { EmailTrainingLabels, EventEmailMessage } from "@/lib/database/emails";
import { Events, EventsAdminData, EventsVenueStats } from "@/lib/database/events";
import { EventVenueCandidate, StaticVenue } from "@/lib/database/venues";
import { getCurrentUser } from "@/lib/db/users";
import {
  notifyEventOwnerChanged,
  notifyVenueCandidateFailure,
  notifyVenueUnarchived,
} from "@/lib/notifications/admin-alert-notify-actions";
import { SlackNotificationType, StripePaymentStatus, VenueType } from "@/lib/shared/enums";
import { Users } from "@/lib/users/types";
import { getLiveExchangeRate } from "@/lib/utils/currency";
import { fetchPOSTNext } from "@/utils/fetch-utils";
import { SupabaseClient } from "@supabase/supabase-js";
import moment from "moment";
import { LineItem } from "../../../components/ui/admin-dash/AdminInvoiceGenerator";
import { getEvent, getOrCreateUserOrgId, getUserFromId } from "./database";

/**
 * Query based on email typed
 */
export async function getUsersFromEmailString(supabase: SupabaseClient, email: string): Promise<Users[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("users")
    .select("*")
    .ilike("email", `%${email}%`)
    .order("user_id", { ascending: false })
    .limit(15);
  if (error) {
    console.log(error);
    return null;
  }

  return data;
}

/**
 * Search users by name, email, or user_id
 * @param supabase - The Supabase client
 * @param searchTerm - The search term (can be name, email, or numeric user_id)
 * @returns Array of matching users or null on error
 */
export async function searchUsers(supabase: SupabaseClient, searchTerm: string): Promise<Users[] | null> {
  const trimmedTerm = searchTerm.trim();
  const isNumeric = /^\d+$/.test(trimmedTerm);
  const escapedTerm = escapeIlike(trimmedTerm);

  let query = supabase.schema("nowadays").from("users").select("*");

  if (isNumeric) {
    const numericId = parseInt(trimmedTerm, 10);
    query = query.or(`user_id.eq.${numericId},email.ilike.%${escapedTerm}%,full_name.ilike.%${escapedTerm}%`);
  } else {
    query = query.or(`email.ilike.%${escapedTerm}%,full_name.ilike.%${escapedTerm}%`);
  }

  const { data, error } = await query.order("user_id", { ascending: false }).limit(15);

  if (error) {
    console.error("Error searching users:", error);
    return null;
  }
  return data;
}

/**
 * Get email from user id
 */
export async function getEmailsFromOrgId(
  supabase: SupabaseClient,
  orgId: number
): Promise<{ first_name: string; last_name: string; email: string }[] | null> {
  // Get user_ids from the org_users table
  const { data: orgData, error: orgError } = await supabase
    .schema("nowadays")
    .from("org_users")
    .select("user_id")
    .eq("org_id", orgId);

  if (orgError) {
    console.log(orgError);
    return null;
  }

  // Extract user_ids
  const userIds = orgData.map((item) => item.user_id);

  // Get emails from the users table
  const { data: userData, error: userError } = await supabase
    .schema("nowadays")
    .from("users")
    .select("first_name, last_name, email")
    .in("user_id", userIds);

  if (userError) {
    console.log(userError);
    return null;
  }

  // Extract emails
  const users = userData.map((item) => ({
    first_name: item.first_name,
    last_name: item.last_name,
    email: item.email,
  }));

  return users;
}

/**
 * Returns a list of all events that exist, along with the user that created each event
 *
 * Uses a api route to load this faster
 */
export async function getAllEvents(
  withTestEvents: boolean = false,
  specificEventId?: number,
  limit?: number,
  offset?: number
): Promise<{ event: Events; user: Users }[] | null> {
  const res = await fetchPOSTNext("/api/admin/get_all_events_with_owners", {
    withTestEvents,
    specificEventId,
    limit,
    offset,
  });

  if (res.ok) {
    return await res.json();
  }

  return null;
}

export interface EventWithSearchData {
  event: Events;
  user: Users;
  org_name?: string;
  booked_venues?: Array<{
    venue_name: string;
    venue_static_id: number;
  }>;
  all_venue_names?: string[];
}

/**
 * Get all events with enhanced search data including organization names and booked venues
 * This is optimized for scalable searching in the admin UI
 */
export async function getAllEventsWithSearchData(
  supabase: SupabaseClient,
  withTestEvents: boolean = false,
  limit?: number,
  offset?: number
): Promise<EventWithSearchData[] | null> {
  // First get all events with their owners
  const eventsWithOwners = await getAllEvents(withTestEvents, undefined, limit, offset);
  if (!eventsWithOwners) return null;

  // Get unique org IDs
  const orgIds = [...new Set(eventsWithOwners.map((e) => e.event.org_id))];

  // Batch fetch organization names
  const orgNamesMap = new Map<number, string>();
  if (orgIds.length > 0) {
    const { data: orgs, error: orgError } = await supabase
      .schema("nowadays")
      .from("orgs")
      .select("org_id, org_name")
      .in("org_id", orgIds);

    if (!orgError && orgs) {
      orgs.forEach((org) => {
        orgNamesMap.set(org.org_id, org.org_name);
      });
    }
  }

  // Get all event IDs
  const eventIds = eventsWithOwners.map((e) => e.event.event_id);

  // Batch fetch ALL venues for all events (for search) and booked venues separately
  const bookedVenuesMap = new Map<number, Array<{ venue_name: string; venue_static_id: number }>>();
  const allVenueNamesMap = new Map<number, string[]>();

  if (eventIds.length > 0) {
    // Fetch all venue candidates (not just booked)
    const { data: allCandidates, error: allVenueError } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .select(
        `
        event_id,
        venue_static_id,
        booked,
        venue_static (
          venue_static_id,
          venue_name
        )
      `
      )
      .in("event_id", eventIds)
      .eq("archived", false);

    if (allVenueError) {
      console.error("Error fetching venue candidates:", allVenueError);
    }

    // Process candidates even if empty array
    if (allCandidates) {
      allCandidates.forEach((candidate: any) => {
        if (candidate.venue_static?.venue_name) {
          const eventId = candidate.event_id;

          // Add to all venues map for search
          if (!allVenueNamesMap.has(eventId)) {
            allVenueNamesMap.set(eventId, []);
          }
          if (!allVenueNamesMap.get(eventId)!.includes(candidate.venue_static.venue_name)) {
            allVenueNamesMap.get(eventId)!.push(candidate.venue_static.venue_name);
          }

          // Add to booked venues map if booked
          if (candidate.booked) {
            if (!bookedVenuesMap.has(eventId)) {
              bookedVenuesMap.set(eventId, []);
            }
            bookedVenuesMap.get(eventId)!.push({
              venue_name: candidate.venue_static.venue_name,
              venue_static_id: candidate.venue_static_id,
            });
          }
        }
      });
    }
  }

  // Combine all data
  return eventsWithOwners.map((item) => ({
    event: item.event,
    user: item.user,
    org_name: orgNamesMap.get(item.event.org_id),
    booked_venues: bookedVenuesMap.get(item.event.event_id) || [],
    all_venue_names: allVenueNamesMap.get(item.event.event_id) || [],
  }));
}

/**
 * Makes `user` the owner of `event`: moves the event into their org, demotes existing owners to
 * admin, and posts the admin "owner changed" Slack card.
 *
 * @param supabase - The Supabase client to write with.
 * @param event - The event being transferred.
 * @param user - The new owner.
 * @returns The updated event row, or `null` when the org lookup or update failed.
 */
export async function setEventOwner(supabase: SupabaseClient, event: Events, user: Users): Promise<Events | null> {
  // Get or create the user's org ID
  const org_id = await getOrCreateUserOrgId(supabase, user);

  if (!org_id) {
    console.log("Failed to get or create user's org");
    return null;
  }

  // Update the event to the new org
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events")
    .update({
      org_id: org_id,
    } as Partial<Events>)
    .eq("event_id", event.event_id)
    .select("*");

  if (error || data.length !== 1) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return null;
  }

  // Get the current owner for notification
  const { data: currentOwnerData } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("user_id")
    .eq("event_id", event.event_id)
    .eq("access_type", "owner")
    .single();

  let previousOwner: Users | null = null;
  if (currentOwnerData?.user_id) {
    previousOwner = await getUserFromId(supabase, currentOwnerData.user_id);
  }

  // First, demote existing owners to admin
  const { error: demoteError } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .update({ access_type: "admin" })
    .eq("event_id", event.event_id)
    .eq("access_type", "owner");

  if (demoteError) {
    console.error("Error demoting existing owners:", demoteError);
  }

  // Check if the new owner already has access to this event
  const { data: existingAccess } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("*")
    .eq("event_id", event.event_id)
    .eq("user_id", user.user_id)
    .single();

  if (existingAccess) {
    // Update existing access to owner
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("user_event_access")
      .update({
        access_type: "owner",
        granted_by: user.user_id,
      })
      .eq("event_id", event.event_id)
      .eq("user_id", user.user_id);

    if (updateError) {
      console.error("Error updating user permissions:", updateError);
    }
  } else {
    // Create new owner access
    const { error: permError } = await supabase.schema("nowadays").from("user_event_access").insert({
      // Don't set id - let the database auto-generate it
      user_id: user.user_id,
      event_id: event.event_id,
      access_type: "owner",
      granted_by: user.user_id,
    });

    if (permError) {
      console.error("Error creating user permissions:", permError);
    }
  }

  // Best-effort admin Slack card (the server action verifies the change and reads names itself)
  await notifyEventOwnerChanged({
    eventId: event.event_id,
    newOwnerUserId: user.user_id,
    previousOwnerUserId: previousOwner?.user_id ?? null,
  }).catch((notifyError) => {
    console.error("setEventOwner: failed to send owner-changed notification", notifyError);
  });

  console.log(`Event owner updated: ${event.event_id} to org ${org_id}`);
  return data[0];
}

export async function getEventOwner(supabase: SupabaseClient, event: Events): Promise<Users | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events_with_owners_org_unique")
    .select("users")
    .eq("event_id", event.event_id)
    .single();

  if (error) {
    console.error("Error fetching event owner:", error);
    return null;
  }

  return data?.users || null;
}

/**
 * Get the actual event owner from user_event_access table
 */
export async function getEventOwnerFromAccess(supabase: SupabaseClient, eventId: number): Promise<Users | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("user_id")
    .eq("event_id", eventId)
    .eq("access_type", "owner")
    .single();

  if (error) {
    console.error("Error fetching event owner from access:", error);
    return null;
  }

  if (!data?.user_id) {
    return null;
  }

  // Now fetch the user details
  const { data: userData, error: userError } = await supabase
    .schema("nowadays")
    .from("users")
    .select("*")
    .eq("user_id", data.user_id)
    .single();

  if (userError) {
    console.error("Error fetching user details:", userError);
    return null;
  }

  return userData;
}

/**
 * Get event owners for multiple events from user_event_access table
 */
export async function getEventOwnersFromAccess(
  supabase: SupabaseClient,
  eventIds: number[]
): Promise<Map<number, Users>> {
  const ownersMap = new Map<number, Users>();

  if (eventIds.length === 0) {
    return ownersMap;
  }

  // Fetch all owner access entries
  const { data: accessData, error: accessError } = await supabase
    .schema("nowadays")
    .from("user_event_access")
    .select("event_id, user_id")
    .in("event_id", eventIds)
    .eq("access_type", "owner");

  if (accessError || !accessData) {
    console.error("Error fetching event owners from access:", accessError);
    return ownersMap;
  }

  // Get unique user IDs
  const userIds = [...new Set(accessData.map((a) => a.user_id))];

  if (userIds.length === 0) {
    return ownersMap;
  }

  // Fetch all user details
  const { data: usersData, error: usersError } = await supabase
    .schema("nowadays")
    .from("users")
    .select("*")
    .in("user_id", userIds);

  if (usersError || !usersData) {
    console.error("Error fetching user details:", usersError);
    return ownersMap;
  }

  // Create user map for quick lookup
  const userMap = new Map<number, Users>();
  usersData.forEach((user) => userMap.set(user.user_id, user));

  // Build the owners map
  accessData.forEach((access) => {
    const user = userMap.get(access.user_id);
    if (user) {
      ownersMap.set(access.event_id, user);
    }
  });

  return ownersMap;
}

export async function getEventAdminData(supabase: SupabaseClient): Promise<EventsAdminData[] | null> {
  const { data, error } = await supabase.schema("nowadays").from("events_admin_data").select(`*`);
  if (error) {
    console.log(error);
    return null;
  }

  return data;
}

export async function getEventVenueStats(supabase: SupabaseClient): Promise<EventsVenueStats[] | null> {
  const { data, error } = await supabase.schema("nowadays").from("events_venue_stats_materialized").select(`*`);
  if (error) {
    console.log(error);
    return null;
  }

  return data;
}

// get the admin metadata for the admin dashboard, or create it if it doesn't exist
export async function getOrCreateEventAdminData(
  supabase: SupabaseClient,
  events: Events[]
): Promise<{ event: Events; eventAdminData: EventsAdminData }[] | null> {
  const { data, error } = await supabase.schema("nowadays").from("events_admin_data").select(`*`);
  if (error) {
    console.log(error);
    return null;
  }

  const adminData: EventsAdminData[] = data;
  const adminDataMapping = new Map<number, EventsAdminData>();
  adminData.forEach((data) => adminDataMapping.set(data.event_id, data));

  return await Promise.all(
    events.map(async (event) => {
      if (adminDataMapping.has(event.event_id)) {
        return {
          event: event,
          eventAdminData: adminDataMapping.get(event.event_id)!,
        };
      } else {
        const { data: event_admin, error } = await supabase
          .schema("nowadays")
          .from("events_admin_data")
          .upsert([
            {
              event_id: event.event_id,
            } as Partial<EventsAdminData>,
          ])
          .select();
        if (error) {
          throw new Error(JSON.stringify(error));
        }
        return {
          event: event,
          eventAdminData: event_admin[0] as EventsAdminData,
        };
      }
    })
  );
}

export async function updateEventAdminData(
  supabase: SupabaseClient,
  eventAdminData: EventsAdminData
): Promise<boolean> {
  const { error } = await supabase
    .schema("nowadays")
    .from("events_admin_data")
    .update([eventAdminData])
    .eq("event_id", eventAdminData.event_id);
  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return false;
  }

  return true;
}

/**
 * Returns a list of all hotels, limiting to 999
 */
export async function getAllHotels(supabase: SupabaseClient): Promise<StaticVenue[] | null> {
  // const { data, error } = await supabase.schema("nowadays").from('venue_static').select(`*`).limit(999);
  const { data, error } = await supabase.schema("nowadays").from("venue_static").select(`*`);
  if (error) {
    console.log(error);
    return null;
  }

  return data;
}

/**
 * Returns a list of all hotels in a list of cities
 */
export async function getAllVenuesInListOfCities(
  supabase: SupabaseClient,
  cities: string[],
  venueTypes: VenueType[]
): Promise<{ venue: StaticVenue; miscReadonlyData: { event_ids: number[] } }[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_static_with_previous_quotes")
    .select(`*`)
    .in("city", cities)
    .in("venue_type", venueTypes);

  if (error) {
    console.log(error);
    return null;
  }

  console.log("data is", data);
  console.log(data);

  // Extract unique chain_ids and brand_ids
  const chainIds = [...new Set(data.map((v) => v.chain_id).filter(Boolean))];
  const brandIds = [...new Set(data.map((v) => v.brand_id).filter(Boolean))];

  // Fetch chain and brand names
  const [chains, brands] = await Promise.all([
    chainIds.length
      ? supabase.schema("nowadays").from("venue_chains").select("chain_id, chain_name").in("chain_id", chainIds)
      : { data: [] },
    brandIds.length
      ? supabase.schema("nowadays").from("venue_chain_brands").select("brand_id, brand_name").in("brand_id", brandIds)
      : { data: [] },
  ]);

  const chainMap = new Map((chains.data || []).map((c) => [c.chain_id, c.chain_name]));
  const brandMap = new Map((brands.data || []).map((b) => [b.brand_id, b.brand_name]));

  return data.map((data) => {
    const dataCopy = { ...data };
    const miscReadonlyData = {
      event_ids: (data.event_ids as (number | null)[]).filter((data) => data !== null),
      previous_nightly_rates: data.previous_nightly_rates,
    };
    delete dataCopy.event_ids;
    return {
      venue: {
        ...dataCopy,
        chain_name: dataCopy.chain_id ? chainMap.get(dataCopy.chain_id) || undefined : undefined,
        brand_name: dataCopy.brand_id ? brandMap.get(dataCopy.brand_id) || undefined : undefined,
      },
      miscReadonlyData: miscReadonlyData as { event_ids: number[] },
    };
  });
}

/**
 * Returns a list of all hotels in a list of cities
 */
export async function getAllHotelsInWithVenueNameMatching(
  supabase: SupabaseClient,
  venueNameLike: string
): Promise<StaticVenue[] | null> {
  // check if user is admin before using service role
  const { data, error } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select(`*`)
    .ilike("venue_name", `%${venueNameLike}%`)
    .limit(200);

  if (error) {
    console.log(error);
    return null;
  }
  console.log(data);
  console.log("hello");

  return data;
}

export async function deleteSpecificHotelImagesFromDatabase(
  supabase: SupabaseClient,
  venue_static_id: number,
  filename: string
): Promise<boolean> {
  // First get the current venue data to check num_photos
  const { data: venueData, error: venueError } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select("num_photos")
    .eq("venue_static_id", venue_static_id)
    .single();

  if (venueError) {
    console.error(`Error getting venue data: ${venueError.message}`);
    return false;
  }

  // Delete the image and clear cache
  const [{ data: deleteData, error: deleteError }, _] = await Promise.all([
    supabase.storage.from("venue_photos").remove([`${venue_static_id}/${filename}`]),
    // erase from cache
    supabase
      .schema("nowadays")
      .from("venue_photo_signed_urls")
      .delete()
      .eq("venue_static_id", venue_static_id)
      .eq("filename", filename),
  ]);

  if (deleteError) {
    console.error(`Error deleting file ${filename}:`, JSON.stringify(deleteError, null, 2));
    return false;
  } else {
    console.log(`Deleted image ${filename} for hotel ${venue_static_id}:`, deleteData);
  }

  // Calculate new photo count (ensure it doesn't go below 0)
  const currentPhotos = venueData?.num_photos || 0;
  const newPhotoCount = Math.max(0, currentPhotos - 1);

  // Update the venue with decremented photo count
  const updateSuccess = await editHotel(supabase, {
    venue_static_id: venue_static_id,
    num_photos: newPhotoCount,
    cover_photo_url: undefined, // erase cache for hotel
    cover_photo_url_expiration: undefined,
  });

  if (!updateSuccess) {
    console.error(`Failed to update num_photos for hotel ${venue_static_id}`);
    return false;
  } else {
    console.log(`Updated num_photos from ${currentPhotos} to ${newPhotoCount} for hotel ${venue_static_id}`);
  }

  return true;
}

export async function setPhotoCaption(
  supabase: SupabaseClient,
  venue: StaticVenue,
  filename: string,
  caption: string | null
) {
  const existingCaption = await supabase
    .schema("nowadays")
    .from("venue_photo_captions")
    .select("*")
    .eq("venue_static_id", venue.venue_static_id)
    .eq("filename", filename);

  if (existingCaption.data && existingCaption.data.length === 1) {
    if (caption === null) {
      const data = await supabase
        .schema("nowadays")
        .from("venue_photo_captions")
        .delete()
        .eq("venue_static_id", venue.venue_static_id)
        .eq("filename", filename);
      if (data.error) {
        console.log(data.error);
      }
    } else {
      const data = await supabase
        .schema("nowadays")
        .from("venue_photo_captions")
        .update({
          caption: caption,
        })
        .eq("venue_static_id", venue.venue_static_id)
        .eq("filename", filename);
      if (data.error) {
        console.log(data.error);
      }
    }
  } else {
    const data = await supabase.schema("nowadays").from("venue_photo_captions").insert({
      venue_static_id: venue.venue_static_id,
      filename: filename,
      caption: caption,
    });
    if (data.error) {
      console.log(data.error);
    }
  }
}

/**
 * Sets the filename of the hotel cover photo, for both the static AND the publicly facing table, if applicable
 *
 * Invalidates the current cover photos
 *
 * @returns true if succeeded, false otherwise
 */
export async function setHotelCoverPhotoFilename(
  supabase: SupabaseClient,
  venue_static_id: number,
  filename: string
): Promise<boolean> {
  const { error } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .update({
      cover_photo_default_filename: filename,
      cover_photo_url: null,
      cover_photo_url_expiration: null,
    })
    .eq("venue_static_id", venue_static_id);

  if (error) {
    console.error("Error updating hotel:", JSON.stringify(error, null, 2));
    return false;
  }

  return true;
}

export async function deleteHotelImagesFromDatabase(
  supabase: SupabaseClient,
  venue_static_ids: number[]
): Promise<boolean> {
  // Delete the image storage bucket for each hotel
  for (const venue_static_id of venue_static_ids) {
    console.log(`Attempting to delete images for hotel ${venue_static_id}`);
    // List the files in the folder
    const { data: fileList, error: listError } = await supabase.storage
      .from("venue_photos")
      .list(`${venue_static_id}/`, {
        limit: 100,
        offset: 0,
        sortBy: { column: "name", order: "asc" },
      });

    console.log(fileList);

    if (listError) {
      console.error("Error listing files:", JSON.stringify(listError, null, 2));
      return false;
    }

    for (const file of fileList) {
      const { data: deleteData, error: deleteError } = await supabase.storage
        .from("venue_photos")
        .remove([`${venue_static_id}/${file.name}`]);

      console.log(`${venue_static_id}/${file.name}`);
      if (deleteError) {
        console.error(`Error deleting file ${file.name}:`, JSON.stringify(deleteError, null, 2));
        return false;
      } else {
        console.log(`Deleted images for hotel ${venue_static_id}:`, deleteData);
      }
    }

    // Use editHotel to update num_photos to 0 after successfully deleting images, and clear photos_manually_verified
    const updateSuccess = await editHotel(supabase, {
      venue_static_id: venue_static_id,
      num_photos: 0,
      photos_manually_verified: false,
      cover_photo_url: undefined, // erase cache for hotel
      cover_photo_url_expiration: undefined,
    });

    if (!updateSuccess) {
      console.error(`Failed to update num_photos for hotel ${venue_static_id}`);
      return false;
    } else {
      console.log(`Updated num_photos to 0 for hotel ${venue_static_id}`);
    }
  }

  return true;
}

// HOW TO SEE WHAT TABLES THAT NEED TO BE INCLUDED IN MERGE

// -- List columns named 'venue_static_id' in the nowadays schema with constraints and referenced tables (if FK)
// SELECT DISTINCT
//     c.table_name,
//     c.column_name,
//     c.data_type,
//     tc.constraint_type,
//     ccu.table_name AS referenced_table_name
// FROM information_schema.columns c
// LEFT JOIN information_schema.key_column_usage kcu
//     ON c.table_name = kcu.table_name
//     AND c.column_name = kcu.column_name
//     AND c.table_schema = kcu.table_schema
// LEFT JOIN information_schema.table_constraints tc
//     ON kcu.constraint_name = tc.constraint_name
//     AND kcu.table_schema = tc.table_schema
// LEFT JOIN information_schema.constraint_column_usage ccu
//     ON tc.constraint_name = ccu.constraint_name
//     AND tc.table_schema = ccu.table_schema
// WHERE c.table_schema = 'nowadays'
//   AND c.column_name = 'venue_static_id'
//   AND tc.constraint_type IS NOT NULL
// ORDER BY c.table_name;

export async function mergeVenueStatic(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newMergedHotel: StaticVenue
) {
  // Run all independent database updates in parallel for speed
  const [
    updatedVenueStaticAccessible,
    updatedVenueStaticGeocoded,
    updatedVenueQuotes,
    updatedExtractedQuotes,
    updatedExtractedQuotesModified,
    updatedCustomerVenueAnswers,
    updatedVenuePhotoCaptions,
    updatedEventVenueCandidates,
    updatedVenuePhotoSignedUrls,
    updateMiscEmailTables,
    updatedVenueStaticMeetingSpace,
    updatedVenueCommunications,
    updatedEmaCandidateThreads,
    updatedEventVenueBookings,
    updatedInvoiceFiles,
    updatedAvaWorkflows,
    updatedWikReviewsAndPosts,
    updatedOeeQuotes,
    updatedVenueStaticMeetingSpaceDetailed,
  ] = await Promise.all([
    updateVenueStaticAccessibleDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenueStaticGeocodedDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenueQuotesDuringMerge(supabase, oldHotels, newMergedHotel),
    updateExtractedQuotesDuringMerge(supabase, oldHotels, newMergedHotel),
    updateExtractedQuotesModifiedDuringMerge(supabase, oldHotels, newMergedHotel),
    updateCustomerVenueAnswersDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenuePhotoCaptionsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateEventVenueCandidateDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenuePhotoSignedUrlsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateMiscEmailTablesDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenueStaticMeetingSpaceDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenueCommunicationsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateEmaCandidateThreadsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateEventVenueBookingsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateInvoiceFilesDuringMerge(supabase, oldHotels, newMergedHotel),
    updateAvaWorkflowsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateWikReviewsAndPostsDuringMerge(supabase, oldHotels, newMergedHotel),
    updateOeeQuotesDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenueStaticMeetingSpaceDetailedDuringMerge(supabase, oldHotels, newMergedHotel),
  ]);

  // Handle storage bucket operations separately (these can be slow with many files)
  const [updatedVenuePhotos, updatedVenueInvoices] = await Promise.all([
    copyVenueImagesFromOldHotelsToNewHotelDuringMerge(supabase, oldHotels, newMergedHotel),
    updateVenueInvoicesDuringMerge(supabase, oldHotels, newMergedHotel),
  ]);

  // Delete old venues last, after all references have been updated
  const deletedVenueStatic = await deleteVenueStaticDuringMerge(supabase, oldHotels);

  return {
    updatedVenueStaticAccessible,
    updatedVenueStaticGeocoded,
    updatedVenueQuotes,
    updatedExtractedQuotes,
    updatedExtractedQuotesModified,
    updatedCustomerVenueAnswers,
    updatedVenuePhotoCaptions,
    updatedEventVenueCandidates,
    updatedVenuePhotos,
    updatedVenuePhotoSignedUrls,
    updatedVenueInvoices,
    deletedVenueStatic,
    updateMiscEmailTables,
    updatedVenueStaticMeetingSpace,
    updatedVenueCommunications,
    updatedEmaCandidateThreads,
    updatedEventVenueBookings,
    updatedInvoiceFiles,
    updatedAvaWorkflows,
    updatedWikReviewsAndPosts,
    updatedOeeQuotes,
    updatedVenueStaticMeetingSpaceDetailed,
  };
}

export async function updateVenueStaticAccessibleDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch delete: delete all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("venue_static_accessible")
      .delete()
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error deleting from venue_static_accessible", error);
      return {
        success: false,
        message: "Error deleting from venue_static_accessible",
      };
    }

    return {
      success: true,
      message: `Venue_static_accessible updated successfully. Deleted ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateVenueStaticAccessible", error);
    return {
      success: false,
      message: "Unexpected error in updateVenueStaticAccessible",
    };
  }
}

export async function updateVenueStaticGeocodedDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    const oldHotelIds = oldHotels.map((hotel) => hotel.venue_static_id);

    // Fetch all geocoded entries for old hotels in venue_static_geocoded
    const { data: geocodedData, error: geocodedError } = await supabase
      .schema("nowadays")
      .from("venue_static_geocoded")
      .select("*")
      .in("venue_static_id", oldHotelIds);

    if (geocodedError) {
      console.log("Error fetching venue_static_geocoded", geocodedError);
      return {
        success: false,
        message: "Error fetching venue_static_geocoded",
      };
    }

    if (geocodedData.length > 0) {
      // Update the first found entry to the new hotel ID
      const firstGeocodedEntry = geocodedData[0];
      const { error: geocodedUpdateError } = await supabase
        .schema("nowadays")
        .from("venue_static_geocoded")
        .update({ venue_static_id: newHotel.venue_static_id })
        .eq("venue_static_id", firstGeocodedEntry.venue_static_id);

      if (geocodedUpdateError) {
        console.log("Error updating venue_static_geocoded", geocodedUpdateError);
        return {
          success: false,
          message: "Error updating venue_static_geocoded",
        };
      }

      // Delete the rest of the entries
      const geocodedEntriesToDelete = geocodedData.slice(1).map((entry) => entry.venue_static_id);
      if (geocodedEntriesToDelete.length > 0) {
        const { error: geocodedDeleteError } = await supabase
          .schema("nowadays")
          .from("venue_static_geocoded")
          .delete()
          .in("venue_static_id", geocodedEntriesToDelete);

        if (geocodedDeleteError) {
          console.log("Error deleting venue_static_geocoded", geocodedDeleteError);
          return {
            success: false,
            message: "Error deleting venue_static_geocoded",
          };
        }
      }
    }

    // Fetch all geocoded entries for old hotels in venue_static_geocoded_google_maps
    const { data: googleMapsData, error: googleMapsError } = await supabase
      .schema("nowadays")
      .from("venue_static_geocoded_google_maps")
      .select("*")
      .in("venue_static_id", oldHotelIds);

    if (googleMapsError) {
      console.log("Error fetching venue_static_geocoded_google_maps", googleMapsError);
      return {
        success: false,
        message: "Error fetching venue_static_geocoded_google_maps",
      };
    }

    if (googleMapsData.length > 0) {
      // Update the first found entry to the new hotel ID
      const firstGoogleMapsEntry = googleMapsData[0];
      const { error: googleMapsUpdateError } = await supabase
        .schema("nowadays")
        .from("venue_static_geocoded_google_maps")
        .update({ venue_static_id: newHotel.venue_static_id })
        .eq("venue_static_id", firstGoogleMapsEntry.venue_static_id);

      if (googleMapsUpdateError) {
        console.log("Error updating venue_static_geocoded_google_maps", googleMapsUpdateError);
        return {
          success: false,
          message: "Error updating venue_static_geocoded_google_maps",
        };
      }

      // Delete the rest of the entries
      const googleMapsEntriesToDelete = googleMapsData.slice(1).map((entry) => entry.venue_static_id);
      if (googleMapsEntriesToDelete.length > 0) {
        const { error: googleMapsDeleteError } = await supabase
          .schema("nowadays")
          .from("venue_static_geocoded_google_maps")
          .delete()
          .in("venue_static_id", googleMapsEntriesToDelete);

        if (googleMapsDeleteError) {
          console.log("Error deleting venue_static_geocoded_google_maps", googleMapsDeleteError);
          return {
            success: false,
            message: "Error deleting venue_static_geocoded_google_maps",
          };
        }
      }
    }

    return {
      success: true,
      message: `Venue_static_geocoded and venue_static_geocoded_google_maps updated successfully.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateVenueStaticGeocoded", error);
    return {
      success: false,
      message: "Unexpected error in updateVenueStaticGeocoded",
    };
  }
}

export async function updateVenueQuotesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("venue_quote")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating venue_quote", error);
      return { success: false, message: "Error updating venue_quote" };
    }

    return {
      success: true,
      message: `Venue_quote updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateVenueQuotesDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateVenueQuotesDuringMerge",
    };
  }
}

export async function updateExtractedQuotesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("extracted_quote")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating extracted_quote", error);
      return { success: false, message: "Error updating extracted_quote" };
    }

    return {
      success: true,
      message: `Extracted_quote updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateExtractedQuotesDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateExtractedQuotesDuringMerge",
    };
  }
}

export async function updateExtractedQuotesModifiedDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("extracted_quote_modified")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating extracted_quote_modified", error);
      return {
        success: false,
        message: "Error updating extracted_quote_modified",
      };
    }

    return {
      success: true,
      message: `Extracted_quote_modified updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateExtractedQuotesModifiedDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateExtractedQuotesModifiedDuringMerge",
    };
  }
}

export async function updateCustomerVenueAnswersDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("customer_venue_answers")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating customer_venue_answers", error);
      return {
        success: false,
        message: "Error updating customer_venue_answers",
      };
    }

    return {
      success: true,
      message: `Customer_venue_answers updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateCustomerVenueAnswersDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateCustomerVenueAnswersDuringMerge",
    };
  }
}

export async function updateVenuePhotoCaptionsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("venue_photo_captions")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating venue_photo_captions", error);
      return {
        success: false,
        message: "Error updating venue_photo_captions",
      };
    }

    return {
      success: true,
      message: `Venue_photo_captions updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateVenuePhotoCaptionsDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateVenuePhotoCaptionsDuringMerge",
    };
  }
}

export async function updateVenuePhotoSignedUrlsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  // NOTE: The venue_photo_signed_urls table doesn't exist in the schema
  // and is not needed since venue images are publicly accessible.
  // This function is kept for compatibility but skips the actual operation.
  console.log("Skipping venue_photo_signed_urls cleanup - table not needed for public venue images");
  return {
    success: true,
    message: "Venue_photo_signed_urls cleanup skipped (table not in use)",
  };
}

export async function updateVenueInvoicesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    for (const oldHotel of oldHotels) {
      // List all folders in the venue_invoices bucket
      const { data: folders, error: listError } = await supabase.storage.from("venue_invoices").list("", {
        limit: 100,
        offset: 0,
        sortBy: { column: "name", order: "asc" },
      });

      if (listError) {
        console.log("Error listing folders in venue_invoices", listError);
        return {
          success: false,
          message: "Error listing folders in venue_invoices",
        };
      }

      for (const folder of folders) {
        // Check if the folder name matches the old venue_static_id
        if (folder.name.includes(`${oldHotel.venue_static_id}`)) {
          const newFolderName = folder.name.replace(`${oldHotel.venue_static_id}`, `${newHotel.venue_static_id}`);

          // Move the folder to the new venue_static_id
          const { error: moveError } = await supabase.storage.from("venue_invoices").move(folder.name, newFolderName);

          if (moveError) {
            console.log(`Error moving folder ${folder.name} to ${newFolderName}`, moveError);
            return {
              success: false,
              message: `Error moving folder ${folder.name} to ${newFolderName}`,
            };
          }
        }
      }
    }
    return {
      success: true,
      message: "Venue_invoices updated successfully",
    };
  } catch (error) {
    console.log("Unexpected error in updateVenueInvoices", error);
    return {
      success: false,
      message: "Unexpected error in updateVenueInvoices",
    };
  }
}

export async function updateEventVenueCandidateDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  let updateCount = 0;
  // get all event_venue_candidate rows for the old hotels
  const { data: candidates, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("*")
    .in(
      "venue_static_id",
      oldHotels.map((hotel) => hotel.venue_static_id)
    );

  if (error) {
    console.log("Error fetching event_venue_candidate", error);
    return { success: false, message: "Error fetching event_venue_candidate" };
  }

  // Group candidates by event_id
  const candidatesByEventId = candidates.reduce(
    (acc, candidate) => {
      if (!acc[candidate.event_id]) {
        acc[candidate.event_id] = [];
      }
      acc[candidate.event_id].push(candidate);
      return acc;
    },
    {} as Record<number, EventVenueCandidate[]>
  );

  for (const eventId in candidatesByEventId) {
    const eventCandidates = candidatesByEventId[eventId];

    // Sort candidates by event_venue_candidate_id to find the most recent one
    eventCandidates.sort(
      (a: EventVenueCandidate, b: EventVenueCandidate) => b.event_venue_candidate_id - a.event_venue_candidate_id
    );

    // Start with the most recent candidate
    const mergedCandidate = {
      ...eventCandidates[0],
    };

    // Fill in missing fields from older candidates
    for (const candidate of eventCandidates.slice(1)) {
      for (const key in candidate) {
        if (mergedCandidate[key] === null || mergedCandidate[key] === undefined) {
          mergedCandidate[key] = candidate[key];
        }
      }
    }

    // Ensure specific fields are handled according to the rules
    mergedCandidate["venue_static_id"] = newHotel.venue_static_id;
    delete mergedCandidate["event_venue_candidate_id"];

    // Handle the email field specifically
    const emailSet = new Set<string>();
    for (const candidate of eventCandidates) {
      const emails = candidate.candidate_contact_scraper_email?.split(",").map((email: string) => email.trim());
      if (emails) {
        for (const email of emails) {
          if (email) {
            emailSet.add(email);
          }
        }
      }
    }

    mergedCandidate.candidate_contact_scraper_email = Array.from(emailSet).join(", ");

    mergedCandidate.candidate_contact_scraper_email = eventCandidates[0].candidate_contact_scraper_email;

    mergedCandidate.show_on_dashboard = eventCandidates.some(
      (candidate: EventVenueCandidate) => candidate.show_on_dashboard
    );

    mergedCandidate.customer_removed = eventCandidates.some(
      (candidate: EventVenueCandidate) => candidate.customer_removed
    );

    mergedCandidate.archived = eventCandidates.some((candidate: EventVenueCandidate) => candidate.archived);

    // create a new candidate in the database
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .insert(mergedCandidate)
      .select();

    if (updateError) {
      console.log("Error creating candidate", updateError);
      return { success: false, message: "Error creating candidate" };
    }

    // delete the old candidates
    const { error: deleteError } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .delete()
      .in(
        "event_venue_candidate_id",
        eventCandidates.map((candidate: EventVenueCandidate) => candidate.event_venue_candidate_id)
      );

    if (deleteError) {
      console.log("Error deleting candidates", deleteError);
      return { success: false, message: "Error deleting candidates" };
    }

    updateCount++;
  }

  return {
    success: true,
    message: `Updated ${updateCount} candidates to new hotel.`,
  };
}

export async function updateMiscEmailTablesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query for each table
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const tables = ["email_training_drafts", "email_training_data", "email_training_labels"];

    const updates: { table: string; count: number }[] = [];

    for (const table of tables) {
      const { error: updateError, count } = await supabase
        .schema("nowadays")
        .from(table)
        .update({ venue_static_id: newHotel.venue_static_id })
        .in("venue_static_id", oldVenueIds);

      if (updateError) {
        console.log(`Error updating ${table}:`, updateError);
        return {
          success: false,
          message: `Error updating ${table}: ${updateError.message}`,
        };
      }

      updates.push({ table, count: count || 0 });
    }

    // Create summary message of all updates
    const updateSummary = updates.map((update) => `${update.table}: ${update.count} entries`).join(", ");

    return {
      success: true,
      message: `Email tables updated successfully. ${updateSummary}`,
    };
  } catch (error) {
    console.log("Unexpected error in updateMiscEmailTablesDuringMerge:", error);
    return {
      success: false,
      message: "Unexpected error in updateMiscEmailTablesDuringMerge",
    };
  }
}

export async function updateVenueStaticMeetingSpaceDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);
    const allVenueIds = [...oldVenueIds, newHotel.venue_static_id];

    console.log(
      `Processing meeting spaces for ${oldVenueIds.length} old venues and new venue ${newHotel.venue_static_id}`
    );

    // Fetch meeting spaces from all venues (old + new)
    const { data: allMeetingSpaces, error: fetchError } = await supabase
      .schema("nowadays")
      .from("venue_static_meeting_space")
      .select("*")
      .in("venue_static_id", allVenueIds);

    if (fetchError) {
      console.error("Fetch error:", fetchError);
      return {
        success: false,
        message: `Error fetching meeting spaces: ${fetchError.message}`,
      };
    }

    const allSpaces = allMeetingSpaces || [];

    // Group meeting spaces by venue_static_id
    const spacesByVenue = new Map<number, any[]>();
    for (const space of allSpaces) {
      const venueId = space.venue_static_id;
      if (!spacesByVenue.has(venueId)) {
        spacesByVenue.set(venueId, []);
      }
      spacesByVenue.get(venueId)!.push(space);
    }

    // Find which venue has the most meeting spaces
    let sourceVenueId: number | null = null;
    let maxCount = 0;

    for (const [venueId, spaces] of spacesByVenue.entries()) {
      if (spaces.length > maxCount) {
        maxCount = spaces.length;
        sourceVenueId = venueId;
      }
    }

    // If no meeting spaces found, just clean up old venues
    if (sourceVenueId === null) {
      const { error: deleteError } = await supabase
        .schema("nowadays")
        .from("venue_static_meeting_space")
        .delete()
        .in("venue_static_id", oldVenueIds);

      if (deleteError) {
        console.error("Delete error:", deleteError);
        return {
          success: false,
          message: `Error deleting meeting spaces: ${deleteError.message}`,
        };
      }

      return {
        success: true,
        message: "No meeting spaces to merge",
      };
    }

    const sourceSpaces = spacesByVenue.get(sourceVenueId) || [];
    const isNewHotelSource = sourceVenueId === newHotel.venue_static_id;

    console.log(
      `Venue ${sourceVenueId} has the most meeting spaces (${maxCount}). ${isNewHotelSource ? "New hotel is source." : "Old hotel is source, transferring to new hotel."}`
    );

    if (!isNewHotelSource) {
      // Source is an old hotel - transfer all spaces to new hotel
      const insertData = sourceSpaces.map((space) => {
        const { venue_static_meeting_space_id, venue_static_id, ...spaceData } = space;
        return {
          ...spaceData,
          venue_static_id: newHotel.venue_static_id,
        };
      });

      const { data: inserted, error: insertError } = await supabase
        .schema("nowadays")
        .from("venue_static_meeting_space")
        .insert(insertData)
        .select();

      if (insertError) {
        console.error("Insert error:", insertError);
        return {
          success: false,
          message: `Error inserting meeting spaces: ${insertError.message}`,
        };
      }

      console.log(`Successfully transferred ${inserted?.length || 0} meeting spaces to new hotel`);

      // Delete all meeting spaces from old venues (including the source)
      const { error: deleteOldError } = await supabase
        .schema("nowadays")
        .from("venue_static_meeting_space")
        .delete()
        .in("venue_static_id", oldVenueIds);

      if (deleteOldError) {
        console.error("Delete error (old):", deleteOldError);
        return {
          success: false,
          message: `Error deleting old meeting spaces: ${deleteOldError.message}`,
        };
      }
    } else {
      // New hotel already has the most - just delete old venues' meeting spaces
      const { error: deleteOldError } = await supabase
        .schema("nowadays")
        .from("venue_static_meeting_space")
        .delete()
        .in("venue_static_id", oldVenueIds);

      if (deleteOldError) {
        console.error("Delete error (old):", deleteOldError);
        return {
          success: false,
          message: `Error deleting old meeting spaces: ${deleteOldError.message}`,
        };
      }
    }

    return {
      success: true,
      message: `Successfully merged meeting spaces. Kept ${maxCount} spaces from venue ${sourceVenueId === newHotel.venue_static_id ? "new hotel" : `old hotel (${sourceVenueId})`}`,
    };
  } catch (error) {
    console.error("Unexpected error:", error);
    return {
      success: false,
      message: `Unexpected error: ${error instanceof Error ? error.message : "Unknown error"}`,
    };
  }
}

export async function updateEmaCandidateThreadsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    // Fetch all records from old venues
    const { data: oldRecords, error: oldFetchError } = await supabase
      .schema("nowadays")
      .from("ema_candidate_threads")
      .select("*")
      .in("venue_static_id", oldVenueIds);

    if (oldFetchError) {
      console.log("Error fetching old venue ema_candidate_threads", oldFetchError);
      return {
        success: false,
        message: "Error fetching old venue ema_candidate_threads",
      };
    }

    if (!oldRecords || oldRecords.length === 0) {
      return {
        success: true,
        message: "No ema_candidate_threads records to update",
      };
    }

    // Fetch all records from new venue to check for conflicts
    const { data: newRecords, error: newFetchError } = await supabase
      .schema("nowadays")
      .from("ema_candidate_threads")
      .select("thread_id, event_id")
      .eq("venue_static_id", newHotel.venue_static_id);

    if (newFetchError) {
      console.log("Error fetching new venue ema_candidate_threads", newFetchError);
      return {
        success: false,
        message: "Error fetching new venue ema_candidate_threads",
      };
    }

    // Create set of existing (thread_id, event_id) pairs for new venue
    const existingPairs = new Set((newRecords || []).map((r) => `${r.thread_id}-${r.event_id}`));

    // Deduplicate old records by (thread_id, event_id) - keep only first occurrence
    const seenPairs = new Map<string, number>(); // Maps key -> first record id
    const conflictingIds: number[] = [];
    const duplicateIds: number[] = [];

    for (const record of oldRecords) {
      const key = `${record.thread_id}-${record.event_id}`;

      if (existingPairs.has(key)) {
        // Conflicts with new venue - delete this
        conflictingIds.push(record.id);
      } else if (seenPairs.has(key)) {
        // Duplicate among old records - delete this (keep the first one)
        duplicateIds.push(record.id);
      } else {
        // First occurrence of this pair - keep it
        seenPairs.set(key, record.id);
      }
    }

    const nonConflictingIds = Array.from(seenPairs.values());
    const toDeleteIds = [...conflictingIds, ...duplicateIds];

    console.log(
      `ema_candidate_threads: ${conflictingIds.length} conflicting with new venue, ${duplicateIds.length} duplicates among old venues, ${nonConflictingIds.length} to update`
    );

    // Delete all records that can't be kept (conflicts + duplicates)
    if (toDeleteIds.length > 0) {
      const { error: deleteError, count: deleteCount } = await supabase
        .schema("nowadays")
        .from("ema_candidate_threads")
        .delete({ count: "exact" })
        .in("id", toDeleteIds);

      if (deleteError) {
        console.log("Error deleting ema_candidate_threads", deleteError);
        return {
          success: false,
          message: "Error deleting ema_candidate_threads",
        };
      }
      console.log(
        `Deleted ${deleteCount} ema_candidate_threads records (${conflictingIds.length} conflicting, ${duplicateIds.length} duplicates)`
      );
    }

    // Update non-conflicting records to point to new venue
    if (nonConflictingIds.length > 0) {
      const { error: updateError, count: updateCount } = await supabase
        .schema("nowadays")
        .from("ema_candidate_threads")
        .update({ venue_static_id: newHotel.venue_static_id })
        .in("id", nonConflictingIds);

      if (updateError) {
        console.log("Error updating ema_candidate_threads", updateError);
        return {
          success: false,
          message: "Error updating ema_candidate_threads",
        };
      }
      console.log(`Updated ${updateCount} ema_candidate_threads records`);
    }

    return {
      success: true,
      message: `ema_candidate_threads updated successfully. Deleted ${toDeleteIds.length} (${conflictingIds.length} conflicts + ${duplicateIds.length} duplicates), updated ${nonConflictingIds.length}.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateEmaCandidateThreadsDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateEmaCandidateThreadsDuringMerge",
    };
  }
}

export async function updateEventVenueBookingsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("event_venue_bookings")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating event_venue_bookings", error);
      return {
        success: false,
        message: "Error updating event_venue_bookings",
      };
    }

    return {
      success: true,
      message: `event_venue_bookings updated successfully. Updated ${count || 0} venues.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateEventVenueBookingsDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateEventVenueBookingsDuringMerge",
    };
  }
}

export async function updateInvoiceFilesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    console.log(`Updating invoice_files: ${oldVenueIds.length} old venues -> new venue ${newHotel.venue_static_id}`);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("invoice_files")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.error("❌ Error updating invoice_files", error);
      return {
        success: false,
        message: `Error updating invoice_files: ${error.message}`,
      };
    }

    console.log(`✓ Updated ${count || 0} invoice_files records`);

    return {
      success: true,
      message: `invoice_files updated successfully. Updated ${count || 0} records.`,
    };
  } catch (error) {
    console.error("❌ Unexpected error in updateInvoiceFilesDuringMerge", error);
    return {
      success: false,
      message: `Unexpected error in updateInvoiceFilesDuringMerge: ${error instanceof Error ? error.message : "Unknown"}`,
    };
  }
}

export async function updateAvaWorkflowsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("ava_workflows")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating ava_workflows", error);
      return {
        success: false,
        message: "Error updating ava_workflows",
      };
    }

    return {
      success: true,
      message: `ava_workflows updated successfully. Updated ${count || 0} venues.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateAvaWorkflowsDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateAvaWorkflowsDuringMerge",
    };
  }
}

export async function updateWikReviewsAndPostsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    // Update wik_reviews
    const { error: reviewsError, count: reviewsCount } = await supabase
      .schema("nowadays")
      .from("wik_reviews")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (reviewsError) {
      console.log("Error updating wik_reviews", reviewsError);
      return {
        success: false,
        message: "Error updating wik_reviews",
      };
    }

    // Update wik_venues_posts
    const { error: postsError, count: postsCount } = await supabase
      .schema("nowadays")
      .from("wik_venues_posts")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (postsError) {
      console.log("Error updating wik_venues_posts", postsError);
      return {
        success: false,
        message: "Error updating wik_venues_posts",
      };
    }

    return {
      success: true,
      message: `WIK tables updated successfully. Reviews: ${reviewsCount || 0}, Posts: ${postsCount || 0}`,
    };
  } catch (error) {
    console.log("Unexpected error in updateWikReviewsAndPostsDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateWikReviewsAndPostsDuringMerge",
    };
  }
}

export async function updateOeeQuotesDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("oee_quotes")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating oee_quotes", error);
      return {
        success: false,
        message: "Error updating oee_quotes",
      };
    }

    return {
      success: true,
      message: `oee_quotes updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateOeeQuotesDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateOeeQuotesDuringMerge",
    };
  }
}

export async function updateVenueStaticMeetingSpaceDetailedDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);
    console.log(`Processing detailed meeting spaces for ${oldVenueIds.length} venues`);

    // Fetch all detailed meeting spaces from old venues
    const { data: allSpaces, error: fetchError } = await supabase
      .schema("nowadays")
      .from("venue_static_meeting_space_detailed")
      .select("*")
      .in("venue_static_id", oldVenueIds);

    if (fetchError) {
      console.error("Error fetching detailed meeting spaces:", fetchError);
      return {
        success: false,
        message: `Error fetching detailed meeting spaces: ${fetchError.message}`,
      };
    }

    if (!allSpaces || allSpaces.length === 0) {
      return {
        success: true,
        message: "No detailed meeting spaces to merge",
      };
    }

    // Group by venue to find which venue has the most detailed meeting spaces
    const spacesByVenue = new Map<number, any[]>();
    for (const space of allSpaces) {
      const venueId = space.venue_static_id;
      if (!spacesByVenue.has(venueId)) {
        spacesByVenue.set(venueId, []);
      }
      spacesByVenue.get(venueId)!.push(space);
    }

    // Find the venue with the most meeting spaces (most complete data)
    // If tied, prefer the most recent (highest venue_static_id as a proxy for recency)
    let bestVenueId: number | null = null;
    let maxSpaces = 0;
    for (const [venueId, spaces] of spacesByVenue.entries()) {
      if (spaces.length > maxSpaces || (spaces.length === maxSpaces && venueId > (bestVenueId || 0))) {
        maxSpaces = spaces.length;
        bestVenueId = venueId;
      }
    }

    if (!bestVenueId) {
      return {
        success: true,
        message: "No detailed meeting spaces to merge",
      };
    }

    console.log(
      `Keeping ${maxSpaces} detailed meeting spaces from venue ${bestVenueId}, discarding ${allSpaces.length - maxSpaces} duplicates`
    );

    // Update the best venue's records to point to the new merged venue
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("venue_static_meeting_space_detailed")
      .update({ venue_static_id: newHotel.venue_static_id })
      .eq("venue_static_id", bestVenueId);

    if (updateError) {
      console.error("Error updating best venue's meeting spaces:", updateError);
      return {
        success: false,
        message: `Error updating meeting spaces: ${updateError.message}`,
      };
    }

    // Delete the duplicate/less complete records from other venues
    const otherVenueIds = oldVenueIds.filter((id) => id !== bestVenueId);
    if (otherVenueIds.length > 0) {
      const { error: deleteError } = await supabase
        .schema("nowadays")
        .from("venue_static_meeting_space_detailed")
        .delete()
        .in("venue_static_id", otherVenueIds);

      if (deleteError) {
        console.error("Error deleting duplicate meeting spaces:", deleteError);
        // Don't fail the whole merge for this
      }
    }

    return {
      success: true,
      message: `Successfully merged ${maxSpaces} detailed meeting spaces (kept most complete from venue ${bestVenueId})`,
    };
  } catch (error) {
    console.error("Unexpected error in updateVenueStaticMeetingSpaceDetailedDuringMerge:", error);
    return {
      success: false,
      message: "Unexpected error in updateVenueStaticMeetingSpaceDetailedDuringMerge",
    };
  }
}

export async function deleteVenueStaticDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[]
): Promise<{ success: boolean; message?: string }> {
  const oldVenueIds = oldHotels.map((h) => h.venue_static_id);
  console.log(`Deleting ${oldVenueIds.length} old venue_static records one-by-one`);

  // Delete one-by-one to avoid statement timeout for venues with many foreign key references
  // This is slower but more reliable than batch deletion
  let totalDeleted = 0;
  const errors: Array<{ venueId: number; error: string }> = [];

  for (let i = 0; i < oldVenueIds.length; i++) {
    const venueId = oldVenueIds[i];
    console.log(`  Deleting venue ${i + 1}/${oldVenueIds.length}: venue_static_id ${venueId}`);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("venue_static")
      .delete({ count: "exact" })
      .eq("venue_static_id", venueId);

    if (error) {
      console.error(`❌ Error deleting venue_static_id ${venueId}`, error);
      errors.push({ venueId, error: error.message });
      // Continue with other deletions instead of failing immediately
      continue;
    }

    totalDeleted += count || 0;
    if ((i + 1) % 10 === 0 || i === oldVenueIds.length - 1) {
      console.log(`  ✓ Progress: ${totalDeleted}/${oldVenueIds.length} records deleted`);
    }
  }

  if (errors.length > 0) {
    const errorSummary = errors.map((e) => `venue_${e.venueId}: ${e.error}`).join("; ");
    console.error(`❌ Failed to delete ${errors.length} venue(s):`);
    errors.forEach((e) => console.error(`   - venue_static_id ${e.venueId}: ${e.error}`));
    return {
      success: false,
      message: `Deleted ${totalDeleted}/${oldVenueIds.length} records. Errors: ${errorSummary}`,
    };
  }

  console.log(`✓ Deleted ${totalDeleted} venue_static records successfully`);
  return { success: true, message: `Venue_static deleted successfully (${totalDeleted} records)` };
}

// returns true if this succeeded and more than 0 images were copied over
export async function copyVenueImagesFromOldHotelsToNewHotelDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  let copyCount = 0;
  // first check if any of the venues have verified images
  const hasVerifiedImages = oldHotels.some((hotel) => hotel.photos_manually_verified);

  // if there are verified images, we need to copy over only the verified images of the venue with the most images
  if (hasVerifiedImages) {
    // get the venue with the most images by listing them from storage
    const venueWithMostImages = await oldHotels.reduce(async (prevPromise, current) => {
      const prev = await prevPromise;
      const prevImageCount = await countAndUpdateNumVenueImagesInStorage(supabase, prev.venue_static_id);
      const currentImageCount = await countAndUpdateNumVenueImagesInStorage(supabase, current.venue_static_id);

      return prevImageCount > currentImageCount ? prev : current;
    }, Promise.resolve(oldHotels[0]));

    // copy over the verified images of the venue with the most images
    const {
      success,
      message,
      copyCount: copyCount2,
    } = await copyVenueImagesToNewHotel(supabase, venueWithMostImages, newHotel, true);

    if (!success) {
      return {
        success: false,
        message: "error copying venue images to new hotel",
      };
    }
    copyCount += copyCount2 ?? 0;
  }
  // if there are no verified images, we can just copy all images
  else if (!hasVerifiedImages) {
    for (const oldHotel of oldHotels) {
      const {
        success,
        message,
        copyCount: copyCount2,
      } = await copyVenueImagesToNewHotel(supabase, oldHotel, newHotel, false);
      if (!success) {
        return {
          success: false,
          message: "error copying venue images to new hotel",
        };
      }
      copyCount += copyCount2 ?? 0;
    }
  }

  for (const oldHotel of oldHotels) {
    // List all files in the venue_photos folder for the current venue_static_id
    const { data: files, error: listError } = await supabase.storage
      .from("venue_photos")
      .list(`${oldHotel.venue_static_id}`);

    if (listError) {
      console.log("Error listing files in venue_photos", listError);
      continue;
    }

    // Delete each file within the folder
    const filePaths = files.map((file) => `${oldHotel.venue_static_id}/${file.name}`);

    if (filePaths.length > 0) {
      const { error: deleteError } = await supabase.storage.from("venue_photos").remove(filePaths);

      if (deleteError) {
        console.log("Error deleting venue_photos", deleteError);
      }
    } else {
      console.log(`No files to delete for venue_static_id: ${oldHotel.venue_static_id}`);
    }
  }

  return {
    success: true,
    message: `Copied ${copyCount} images from ${oldHotels.length} old hotels to ${newHotel.venue_static_id}`,
  };
}

/**
 * Recursively lists all files in a folder path, including subfolders
 */
async function listAllFilesRecursively(
  supabase: SupabaseClient,
  bucket: string,
  folderPath: string,
  allFiles: Array<{ path: string; name: string }> = []
): Promise<Array<{ path: string; name: string }>> {
  const { data: items, error } = await supabase.storage.from(bucket).list(folderPath);

  if (error || !items) {
    console.error(`Error listing ${folderPath}:`, error);
    return allFiles;
  }

  for (const item of items) {
    const fullPath = folderPath ? `${folderPath}/${item.name}` : item.name;

    if (item.id === null) {
      // It's a folder, recurse into it
      await listAllFilesRecursively(supabase, bucket, fullPath, allFiles);
    } else {
      // It's a file
      allFiles.push({ path: fullPath, name: item.name });
    }
  }

  return allFiles;
}

/**
 * Copies venue images/attachments from old hotel to new hotel, handling:
 * - Folder structures (hotel_resources, floor_plans, meeting_rooms_information, etc.)
 * - Deduplication (if file exists, rename with _1, _2, etc.)
 * - Returns count of successfully copied files
 */
export async function copyVenueImagesToNewHotel(
  supabase: SupabaseClient,
  oldHotel: StaticVenue,
  newHotel: StaticVenue,
  verifiedOnly: boolean
): Promise<{ success: boolean; message?: string; copyCount?: number }> {
  let copyCount = 0;

  if (verifiedOnly && !oldHotel.photos_manually_verified) {
    return {
      success: true,
      message: "no verified images to copy",
    };
  }

  // List all files recursively from old hotel folder
  console.log(`Listing all files for venue ${oldHotel.venue_static_id}...`);
  const allFiles = await listAllFilesRecursively(supabase, "venue_photos", `${oldHotel.venue_static_id}`);

  if (allFiles.length === 0) {
    return {
      success: true,
      message: "no images to copy",
    };
  }

  console.log(`Found ${allFiles.length} files to copy from ${oldHotel.venue_static_id} to ${newHotel.venue_static_id}`);

  // Track existing files in destination to handle deduplication
  const existingFiles = await listAllFilesRecursively(supabase, "venue_photos", `${newHotel.venue_static_id}`);
  const existingPaths = new Set(existingFiles.map((f) => f.path.replace(`${newHotel.venue_static_id}/`, "")));

  // Helper function to get unique filename if duplicate exists
  const getUniqueFilename = (relativePath: string): string => {
    if (!existingPaths.has(relativePath)) {
      return relativePath;
    }

    // Split into path parts and filename
    const parts = relativePath.split("/");
    const filename = parts[parts.length - 1];
    const pathPrefix = parts.slice(0, -1).join("/");

    // Split filename into name and extension
    const lastDot = filename.lastIndexOf(".");
    const name = lastDot > 0 ? filename.substring(0, lastDot) : filename;
    const ext = lastDot > 0 ? filename.substring(lastDot) : "";

    // Find available number suffix
    let counter = 1;
    let newFilename: string;
    let newRelativePath: string;

    do {
      newFilename = `${name}_${counter}${ext}`;
      newRelativePath = pathPrefix ? `${pathPrefix}/${newFilename}` : newFilename;
      counter++;
    } while (existingPaths.has(newRelativePath));

    return newRelativePath;
  };

  // Copy each file, handling deduplication
  for (const file of allFiles) {
    // Get relative path (remove venue_static_id prefix)
    const relativePath = file.path.replace(`${oldHotel.venue_static_id}/`, "");
    const uniqueRelativePath = getUniqueFilename(relativePath);

    const sourcePath = file.path;
    const destPath = `${newHotel.venue_static_id}/${uniqueRelativePath}`;

    const { error } = await supabase.storage.from("venue_photos").copy(sourcePath, destPath);

    if (error) {
      console.error(`Error copying ${sourcePath} -> ${destPath}:`, error);
      // Continue with other files instead of failing completely
      continue;
    }

    // Track this as existing for subsequent files
    existingPaths.add(uniqueRelativePath);
    copyCount++;

    if (uniqueRelativePath !== relativePath) {
      console.log(`  ✓ Copied with deduplication: ${relativePath} -> ${uniqueRelativePath}`);
    }
  }

  // Update venue_static with photo metadata
  const { error: error2 } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .update({
      photos_manually_verified: copyCount > 0 ? oldHotel.photos_manually_verified : false,
      num_photos: copyCount, // Update with actual count of successfully copied images
    })
    .eq("venue_static_id", newHotel.venue_static_id);

  if (error2) {
    console.log("Could not update photos manually verified and num_photos for merged venue", error2);
    return {
      success: false,
      message: "Could not update photos manually verified and num_photos for merged venue",
    };
  }

  console.log(
    `✓ Successfully copied ${copyCount}/${allFiles.length} files from venue ${oldHotel.venue_static_id} to ${newHotel.venue_static_id}`
  );

  return {
    success: true,
    message: `Copied ${copyCount} files from ${oldHotel.venue_static_id} to ${newHotel.venue_static_id}`,
    copyCount: copyCount,
  };
}

// returns true if this succeeded
export async function moveOeeOriginalExpectedOutput(
  supabase: SupabaseClient,
  oldHotel: StaticVenue,
  newHotel: StaticVenue
): Promise<boolean> {
  // list old hotel venue images
  // const { error } = await supabase.schema("nowadays").from("oee_original_output").update({
  //     venue_static_id: newHotel.venue_static_id
  // }).eq("venue_static_id", oldHotel.venue_static_id);
  const { error: error2 } = await supabase
    .schema("nowadays")
    .from("extracted_quote_modified")
    .update({
      venue_static_id: newHotel.venue_static_id,
    })
    .eq("venue_static_id", oldHotel.venue_static_id);

  if (error2) {
    // console.log(error);
    console.log(error2);
  }

  return !error2;
}

/**
 * Returns a list of all admin emails.
 */
export async function getAllAdminEmails(supabase: SupabaseClient): Promise<string[]> {
  const { data, error } = await supabase.schema("nowadays").from("admins").select(`email`);
  if (error) {
    console.log(error);
    return [];
  }

  return data.map((d) => d.email);
}

/**
 * Returns a list of all admin Users
 */
export async function getAllAdminUsers(supabase: SupabaseClient): Promise<Users[] | null> {
  const { data, error } = await supabase.schema("nowadays").from("admin_users").select(`*`).order("email");
  if (error) {
    console.log(error);
    return [];
  }

  return data as Users[];
}

/**
 * Updates the last scraper time for a particular hotel candidate id
 */
export async function updateLastScraperTimeToNowCandidate(
  supabase: SupabaseClient,
  event_venue_candidate_id: number
): Promise<void> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select(`*`)
    .eq("event_venue_candidate_id", event_venue_candidate_id);
  if (error) {
    console.log(error);
    return;
  }

  if (data.length !== 1) {
    return;
  }

  await editHotelCandidate(supabase, {
    ...data[0],
    candidate_contact_scraper_email_last_updated: Date.now(),
  });
}

/**
 * Returns the current candidates for a particular event. Is useful in the stage where users are evaluating candidates.
 */
export async function getEventHotelCandidates(
  supabase: SupabaseClient,
  event: Events
): Promise<
  | {
      candidate: EventVenueCandidate;
      hotel: StaticVenue;
    }[]
  | null
> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select(`*, venue_static!event_venue_candidate_venue_static_id_fkey(*)`)
    .eq("event_id", event.event_id);
  if (error) {
    console.log(error);
    return null;
  }

  return data.map((values) => {
    const hotel = values["venue_static"];
    const candidate = values;
    delete candidate["venue_static"];
    return {
      candidate: candidate,
      hotel: hotel,
    };
  });
}

/**
 * Returns a list of the updated venue information for a particular venue
 */
export async function getEventDetailsForVenue(event: Events, hotel: StaticVenue): Promise<StaticVenue> {
  throw new Error("NOT YET IMPLEMENTED");
}

export async function editVenueColumn(): Promise<void> {
  throw new Error("NOT YET IMPLEMENTED");
}

/**
 * Flag/mode options for {@link addHotelsToEvent}, as opposed to the data parameters (client, event,
 * hotel ids) that stay positional.
 */
export interface AddHotelsToEventOptions {
  /**
   * True when called from a server context (API route, server action) rather than client-side.
   * Not currently read inside the function body. Defaults to false.
   */
  runningOnServerSide?: boolean;
  /** Free-form source tag recorded as `source` on each new candidate row (e.g. "admin_dash"). */
  candidateSource?: string;
  /** User id recorded as `added_by_user_id` on each new candidate row. */
  addedByUserId?: number;
  /**
   * When true, every new candidate is given ranking 1 (auto-added), instead of an incrementing
   * ranking starting from the highest existing ranking (or 3 if none exists). Defaults to false.
   */
  autoAdded?: boolean;
  /**
   * When true, every id is inserted without first looking it up, and a venue that is already a
   * candidate is ignored (via upsert with "ignore duplicates") rather than failing the batch.
   * Only for a caller that has already dropped the venues already on the event (a lookup per id
   * is a round trip each). Defaults to false.
   */
  skipExistingCheck?: boolean;
}

/**
 * Admin access to add a hotel to an event, used in api/venues/add_venues_to_event.
 * The supabase client is assumed to be fully authorized (service_role)
 *
 * @param supabaseAuth authenticated supabase client
 * @param event the event to add the hotels to
 * @param hotelIds the venue_static ids to add as candidates
 * @param options flag/mode options, see {@link AddHotelsToEventOptions}
 * @returns true if succeeded, false otherwise
 */
export async function addHotelsToEvent(
  supabaseAuth: SupabaseClient,
  event: Events,
  hotelIds: number[],
  options: AddHotelsToEventOptions = {}
): Promise<boolean> {
  const { candidateSource, addedByUserId, autoAdded = false, skipExistingCheck = false } = options;

  const candidates: any[] = [];

  // Get the highest current ranking to assign new venues a lower ranking
  const { data: existingCandidates } = await supabaseAuth
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("ranking")
    .eq("event_id", event.event_id)
    .order("ranking", { ascending: false })
    .limit(1);

  // Start ranking from the highest existing ranking + 1, or 3 if no candidates exist
  let currentRanking = 3;
  if (existingCandidates && existingCandidates.length > 0 && existingCandidates[0].ranking) {
    currentRanking = Math.max(3, existingCandidates[0].ranking + 1);
  }

  // Extract custom origin name from event's formatted_locations for distance display
  const formattedLocations = event.formatted_locations as any[] | null;
  const location = formattedLocations?.[0];
  let customOriginName = null;
  if (location) {
    if (location.type === "enterprise_office") {
      customOriginName = location.display_name || location.city;
    } else if (location.type === "nearby_address" || location.type === "address") {
      customOriginName = location.formatted_address || location.city;
    } else {
      customOriginName = location.city;
    }
  }

  for (const hotelId of hotelIds) {
    const { data, error } = skipExistingCheck
      ? { data: [], error: null }
      : await supabaseAuth
          .schema("nowadays")
          .from("event_venue_candidate")
          .select("*")
          .eq("event_id", event.event_id)
          .eq("venue_static_id", hotelId);

    if (error) {
      console.error("Error checking hotel:", JSON.stringify(error, null, 2));
      // Send Slack notification for failure
      await notifyVenueCandidateFailure({
        kind: "check_existing",
        eventId: event.event_id,
        venueStaticId: hotelId,
        errorText: JSON.stringify(error),
      }).catch((notifyError) => {
        console.error("Failed to send venue candidate check failure notification", notifyError);
      });
      return false;
    }

    if (data.length === 0) {
      const candidate: Partial<EventVenueCandidate> = {
        event_id: event.event_id,
        venue_static_id: hotelId,
        source: candidateSource,
        added_by_user_id: addedByUserId,
        // Auto-added venues get ranking 1, otherwise assign incrementing ranking starting from 3
        ranking: autoAdded ? 1 : currentRanking++,
        custom_origin_name: customOriginName,
      };
      candidates.push(candidate);
    }
  }

  // If there are no new candidates, return true
  if (candidates.length === 0) {
    return true;
  }

  // Insert new candidates. A caller that skipped the per-id lookup may race another add of the
  // same venue, so it inserts with "ignore duplicates" on the unique (event_id, venue_static_id)
  // constraint: a venue another request just added is left as it is, instead of the one conflicting
  // row failing the whole batch.
  const candidateTable = supabaseAuth.schema("nowadays").from("event_venue_candidate");
  const { error } = skipExistingCheck
    ? await candidateTable.upsert(candidates, { onConflict: "event_id,venue_static_id", ignoreDuplicates: true })
    : await candidateTable.insert(candidates);

  if (error) {
    console.error("Error creating event venue candidates:", JSON.stringify(error, null, 2));
    // Send Slack notification for auto-add failure
    await notifyVenueCandidateFailure({
      kind: "auto_add",
      eventId: event.event_id,
      venueIds: hotelIds,
      candidateCount: candidates.length,
      autoAdded: Boolean(autoAdded),
      errorText: JSON.stringify(error),
    }).catch((notifyError) => {
      console.error("Failed to send venue auto-add failure notification", notifyError);
    });
    return false;
  }

  return true;
}

export async function archiveCandidate(
  supabase: SupabaseClient,
  candidate: EventVenueCandidate,
  archive: boolean = true
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update({
      archived: archive,
    })
    .eq("event_venue_candidate_id", candidate.event_venue_candidate_id);
  if (error) {
    console.log("something went wrong archiving the candidate", error);
    return false;
  }

  return true;
}

// Function to unarchive a candidate and hide it from the dashboard
export async function unarchiveCandidateAndHideFromDash(
  supabase: SupabaseClient,
  candidate: EventVenueCandidate
): Promise<boolean> {
  // Check if the candidate is already archived
  if (candidate.archived) {
    const { error } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .update({
        archived: false, // Unarchive the candidate
        show_on_dashboard: false, // Hide from dashboard
      })
      .eq("event_venue_candidate_id", candidate.event_venue_candidate_id);

    if (error) {
      console.error("Error unarchiving the candidate", error);
      return false;
    }
    // Send a Slack card to the admin with venue static id and event id
    await notifyVenueUnarchived({ eventVenueCandidateId: candidate.event_venue_candidate_id }).catch((notifyError) => {
      console.error("Failed to send venue unarchived notification", notifyError);
    });
    return true;
  }
  // If the candidate is not archived, no action is needed
  return true;
}

export async function archiveHotelsFromEvent(
  supabase: SupabaseClient,
  event: Events,
  hotels: StaticVenue[]
): Promise<boolean> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update({ archived: true }) // Set the archived flag to true
    .eq("event_id", event.event_id)
    .in(
      "venue_static_id",
      hotels.map((hotel) => hotel.venue_static_id)
    );
  console.log(data);
  if (error) {
    console.error("Error archiving hotels:", JSON.stringify(error, null, 2));
    return false;
  }

  return true;
}

/**
 * Updates the num_photos field in the database for a given venue_static_id.
 *
 * @param supabase - The SupabaseClient instance.
 * @param venue_static_id - The ID of the hotel to update.
 * @param imageCount - The new image count to set.
 * @returns A promise that resolves to true if the update was successful, or false otherwise.
 */
export async function updateNumPhotosInDatabase(
  supabase: SupabaseClient,
  venue_static_id: number,
  imageCount: number
): Promise<boolean> {
  // Assuming this snippet is part of a function where `venue_static_id` and `imageCount` are defined
  const updateSuccess = await editHotel(supabase, {
    venue_static_id: venue_static_id,
    num_photos: imageCount,
  });

  if (!updateSuccess) {
    console.error(`Failed to update num_photos for hotel ${venue_static_id}`);
    return false;
  } else {
    console.log(`Updated num_photos for hotel ${venue_static_id} to ${imageCount}`);
    return true;
  }
}

// Check if more than 5 venue images exist in Supabase storage
export async function countAndUpdateNumVenueImagesInStorage(
  supabase: SupabaseClient,
  venue_static_id: number
): Promise<number> {
  const { data, error } = await supabase.storage.from("venue_photos").list(venue_static_id.toString()); // Use list to get all files in the directory

  if (error) {
    console.error("Error listing venue images:", error);
    return -1;
  }

  // console.log("Images found in storage:", data.length);

  // Check if there are more than 5 images
  const imageCount = data.length;

  console.log("Venue static id:", venue_static_id);

  console.log("Image count:", imageCount);

  // Update num_photos in venue_static table
  // TODO: took out the await since it was laggy
  const updateSuccess = updateNumPhotosInDatabase(supabase, venue_static_id, imageCount);

  // if (!updateSuccess) {
  //     console.error(`Failed to update num_photos for hotel ${venue_static_id}`);
  // } else {
  //     // console.log(`Updated num_photos to ${imageCount} for hotel ${venue_static_id}`);
  // }

  return imageCount;
}

export async function uploadHotelImages(
  supabase: SupabaseClient,
  venue_static_ids: number[],
  files: File[]
): Promise<void> {
  for (const venue_static_id of venue_static_ids) {
    // Upload images
    for (const file of files) {
      const path = `${venue_static_id}/${file.name}`;
      const { error } = await supabase.storage.from("venue_photos").upload(path, file);
      if (error) throw error;
    }

    // Count images in storage
    const imageCount = await countAndUpdateNumVenueImagesInStorage(supabase, venue_static_id);
    console.log(`Images have been uploaded, there are now ${imageCount} images for venue_static_id ${venue_static_id}`);
  }
}

export async function addHotelToDatabase(supabase: SupabaseClient, hotels: StaticVenue): Promise<StaticVenue | null> {
  console.log("hotels", hotels);
  // Remove computed/UI fields that don't exist in the database
  delete (hotels as any).candidate;
  delete (hotels as any).quote;
  delete (hotels as any).quote_temp_usd;
  delete (hotels as any).misc_readonly_data;
  delete (hotels as any).miscReadonlyData;
  delete (hotels as any).venue;
  delete (hotels as any).document_counts; // UI-computed field from AdminUI
  delete (hotels as any).email_threads; // Computed field

  const { data, error } = await supabase.schema("nowadays").from("venue_static").insert(hotels).select("*");
  if (error) {
    console.error("Error adding hotel to database:", JSON.stringify(error, null, 2));
    return null;
  }

  return data[0];
}

/**
 * Edits a hotel with new information. Uses the venue_static_id to update information,
 * and all information changed within the object will be applied.
 *
 * @returns true if succeeded, false otherwise
 */
export async function editHotel(supabase: SupabaseClient, newData: Partial<StaticVenue>): Promise<boolean> {
  // List of fields that should not be sent to the database
  // These are computed fields from joins or UI calculations
  const computedFields = ["email_threads", "document_counts", "chain_name", "brand_name", "max_capacity", "quotes"];

  // Remove computed fields and convert empty strings to null
  const sanitizedData = Object.fromEntries(
    Object.entries(newData)
      .filter(([key]) => !computedFields.includes(key))
      .map(([key, value]) => [key, value === "" ? null : value])
  );

  const { error } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .update(sanitizedData)
    .eq("venue_static_id", newData.venue_static_id);

  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return false;
  }

  return true;
}

/**
 * Edits a hotel candidate.
 *
 * @returns true if succeeded, false otherwise
 */
export async function editHotelCandidate(
  supabase: SupabaseClient,
  data: Partial<EventVenueCandidate>
): Promise<boolean> {
  console.log("this is the data upserting", data);

  // Convert empty string values to null
  const sanitizedData = Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value === "" ? null : value])
  );

  const { error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .update([sanitizedData])
    .eq("event_venue_candidate_id", data.event_venue_candidate_id);

  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    return false;
  }

  return true;
}

/**
 * Gets the quote for a particular event and hotel, if it exists
 */
export async function getEventQuoteAny(
  supabase: SupabaseClient,
  event: Events,
  venue_static_id: number,
  before_date?: Date
): Promise<any | null> {
  let query = supabase
    .schema("nowadays")
    .from("venue_options")
    .select(`*`)
    .eq("event_id", event.event_id)
    .eq("venue_static_id", venue_static_id);

  if (before_date) {
    query = query.lt("created_at", before_date);
  }

  const { data, error } = await query;

  if (error) {
    console.log(error);
    return null;
  }

  if (!data || data.length === 0) {
    return null;
  }

  // Extract OEE quote from the view data
  const viewData = data[0];
  const oeeQuote =
    (viewData as any).oee_quote ||
    (viewData as any).estimated_oee_quote ||
    (viewData as any).negotiated_oee_quote ||
    null;

  return oeeQuote;
}

// Function to update event_venue_candidate with a new email and call updateHotelStaticWithEmail
export async function updateCandidateAndVenueWithEmail(
  supabase: SupabaseClient,
  event_venue_candidate_id: number,
  new_email: string
): Promise<void> {
  console.log("IM IN THE FUNCTION AHHH");
  // Fetch the event_venue_candidate data
  const { data: candidateData, error: candidateError } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("event_id, venue_static_id, candidate_contact_scraper_email")
    .eq("event_venue_candidate_id", event_venue_candidate_id)
    .single();

  if (candidateError || !candidateData) {
    console.error("Error fetching event_venue_candidate:", candidateError);
    return;
  }

  // Combine new email with existing emails, avoiding duplicates and excluding @getnowadays.com
  const currentCandidateEmails = candidateData.candidate_contact_scraper_email
    ? candidateData.candidate_contact_scraper_email
        .split(", ")
        .filter((email: string) => !email.includes("@getnowadays.com") && !email.includes("@nowadays.ai"))
    : [];

  if (
    !currentCandidateEmails.includes(new_email) &&
    !new_email.includes("@getnowadays.com") &&
    !new_email.includes("@nowadays.ai")
  ) {
    const updatedCandidateEmails = [...currentCandidateEmails, new_email].join(", ");

    // Update event_venue_candidate with the new email
    const { error: updateCandidateError } = await supabase
      .schema("nowadays")
      .from("event_venue_candidate")
      .update({ candidate_contact_scraper_email: updatedCandidateEmails })
      .eq("event_venue_candidate_id", event_venue_candidate_id);

    if (updateCandidateError) {
      console.error("Error updating event_venue_candidate:", updateCandidateError);
      return;
    } else {
      console.log(`Successfully updated email for event_venue_candidate ${event_venue_candidate_id} with ${new_email}`);
    }
  }
  // Call the existing function to update the venue_static table
  await updateHotelStaticWithEmail(supabase, candidateData.venue_static_id, new_email);
}

// Function to update venue_static with a new email, excluding any @getnowadays.com emails
async function updateHotelStaticWithEmail(
  supabase: SupabaseClient,
  venue_static_id: number,
  new_email: string
): Promise<void> {
  console.log("HOTEL STATIC ID", venue_static_id);

  // Fetch the current email for the venue_static_id
  const { data, error: fetchError } = await supabase
    .schema("nowadays")
    .from("venue_static")
    .select("email")
    .eq("venue_static_id", venue_static_id)
    .single();

  if (fetchError) {
    console.error("Error fetching venue_static email:", fetchError);
    return;
  }

  // Prepare the updated email list, ensuring no duplicates and excluding @getnowadays.com emails
  const currentEmails = data.email
    ? data.email
        .split(", ")
        .filter((email: string) => !email.includes("@getnowadays.com") && !email.includes("@nowadays.ai"))
    : [];
  if (
    !currentEmails.includes(new_email) &&
    !new_email.includes("@getnowadays.com") &&
    !new_email.includes("@nowadays.ai")
  ) {
    const updatedEmails = [...currentEmails, new_email].join(", ");

    // Update the venue_static record with the new email
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("venue_static")
      .update({ email: updatedEmails })
      .eq("venue_static_id", venue_static_id);

    if (updateError) {
      console.error("Error updating venue_static email:", updateError);
    } else {
      console.log(`Successfully updated email for venue_static_id ${venue_static_id} with ${new_email}`);
    }
  }
}

/**
 * Edit an event, overriding event details when specified. All fields other than the event are optional.
 */
export async function editEvent(
  supabase: SupabaseClient,
  event: Events,
  data?: Partial<Events>
): Promise<Events | null> {
  const { data: newData, error } = await supabase
    .schema("nowadays")
    .from("events")
    .update({
      ...data,
    })
    .eq("event_id", event.event_id)
    .select("*");
  if (error) {
    console.error("Error updating event:", JSON.stringify(error, null, 2));
    throw error;
  }
  console.log(`Event updated: ${event.event_id}`);

  return newData[0] ?? null;
}

/**
 * Gets the most recent message from a thread using ema_messages table.
 * Note: The thread_id parameter should be a Gmail thread ID when using ema tables.
 */
export async function getMostRecentEmailThread(
  supabase: SupabaseClient,
  thread_id: string
): Promise<EventEmailMessage | null> {
  // First, find the ema_threads record by gmail_thread_id
  const { data: threadData, error: threadError } = await supabase
    .schema("nowadays")
    .from("ema_threads")
    .select("id")
    .eq("gmail_thread_id", thread_id)
    .single();

  if (threadError || !threadData) {
    console.log("Thread not found in ema_threads:", threadError);
    return null;
  }

  // Get the most recent message from the thread using ema_messages
  const { data: messages, error } = await supabase
    .schema("nowadays")
    .from("ema_messages")
    .select("*, ema_threads!inner(gmail_thread_id, subject)")
    .eq("thread_id", threadData.id)
    .order("sent_at", { ascending: false })
    .limit(1);

  if (error || !messages || messages.length === 0) {
    console.log("Something went wrong getting email thread from ema_messages:", error);
    return null;
  }

  // Using type assertion to handle the joined table data from ema_messages and ema_threads
  const message = messages[0] as any;

  // Convert ema_messages format to EventEmailMessage format
  // Note: EventEmailMessage expects different field names
  const convertedMessage: EventEmailMessage = {
    event_id: "", // ema_messages doesn't have event_id, would need to join with ema_candidate_threads
    event_name: "", // Not available in ema_messages
    event_status: "", // Not available in ema_messages
    message_id: message.gmail_message_id || "",
    thread_id: message.ema_threads?.gmail_thread_id || thread_id,
    subject: message.subject || "",
    snippet: message.snippet || "",
    timestamp: message.sent_at || message.created_at,
    from_address: message.from_address || null,
    to_addresses: message.to_addresses || [],
    cc: message.cc_addresses || [],
    bcc: message.bcc_addresses || [],
    body: message.body || {},
    labels: [], // Labels are not stored in ema_messages
    is_in_inbox: false, // Would need to check Gmail API for current labels
    is_archived: true, // Would need to check Gmail API for current labels
  };

  return convertedMessage;
}

export async function getExampleEmails(
  supabase: SupabaseClient,
  labels: string | string[],
  limit: number = 5
): Promise<EmailTrainingLabels[] | null> {
  // Convert single string to array and split by commas if needed
  const labelArray = Array.isArray(labels) ? labels : labels.split(",").map((l) => l.trim());

  // Convert all labels to uppercase to match EmailActionLabels enum
  const normalizedLabels = labelArray.map((label) => label.toUpperCase());

  const { data, error } = await supabase
    .schema("nowadays")
    .from("email_final_drafts")
    .select("*")
    .in("final_action_labels", normalizedLabels)
    .limit(limit * normalizedLabels.length);

  if (!error) {
    return data;
  } else {
    console.error("ERROR GETTING EXAMPLE EMAILS");
    console.error(error);
    return null;
  }
}

export async function getActiveEvents(supabase: SupabaseClient): Promise<Events[] | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("events_not_hidden_admin")
    .select("*")
    .order("created_at", { ascending: false });
  if (!error) {
    // console.log("EVENTS");
    // console.log(data);
    return data;
  } else {
    console.error("EVENT MOST RECENT ERROR");
    console.error(error);
    return null;
  }
}

// gets EventHotelCandidateID from venue and event id
export async function getEventHotelCandidateId(
  supabase: SupabaseClient,
  venue: StaticVenue,
  eventId: number
): Promise<number | null> {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_venue_candidate")
    .select("event_venue_candidate_id")
    .eq("venue_static_id", venue.venue_static_id)
    .eq("event_id", eventId);

  if (error) {
    console.error("Error fetching event hotel candidate ID:", JSON.stringify(error, null, 2));
    return null;
  }
  if (data.length !== 1) {
    console.error(`Expected one event hotel candidate ID, found ${data.length}`);
    return null;
  }

  return data[0].event_venue_candidate_id;
}

// PAYMENTS
export const addOrUpdateExpectedProfitToPaymentsTableForInvoice = async (
  supabase: SupabaseClient,
  {
    eventId,
    lineItems,
    invoiceNumber,
    currency,
  }: {
    eventId: number | null;
    lineItems: LineItem[];
    invoiceNumber: string;
    currency: string;
  }
): Promise<boolean | { isCommission: true; eventId: number; commissionAmount: number; dueDate: string }> => {
  console.log("Adding or updating expected profit to payments table for invoice:", invoiceNumber);

  // Check if this is a commission invoice
  const isCommissionInvoice = invoiceNumber.toLowerCase().includes("commission");

  // Get exchange rate if not USD
  let exchangeRate = 1;
  if (currency.toUpperCase() !== "USD") {
    const rate = await getLiveExchangeRate(currency);
    if (rate === null) {
      console.error("Failed to get exchange rate, using 1 as fallback");
    } else {
      exchangeRate = rate;
    }
  }

  // If it's a commission invoice, return commission details for modal handling
  if (isCommissionInvoice && eventId) {
    console.log("Commission invoice detected, returning commission details for event update");

    // Calculate total commission amount
    const totalCommission = lineItems.reduce((total, item) => {
      return total + (Number(item.expectedProfit) || 0) * exchangeRate;
    }, 0);

    // Get the latest due date
    const latestDueDate = lineItems
      .reduce(
        (latest, item) => {
          const itemDate = moment(item.dueDate, "YYYY-MM-DD");
          return itemDate.isAfter(latest) ? itemDate : latest;
        },
        moment(lineItems[0].dueDate, "YYYY-MM-DD")
      )
      .format("YYYY-MM-DD");

    // Return commission details
    return {
      isCommission: true,
      eventId,
      commissionAmount: totalCommission,
      dueDate: latestDueDate,
    };
  }

  // Calculate expected profit per month
  const monthlyProfits = lineItems.reduce(
    (acc, item) => {
      const dueDate = moment(item.dueDate, "YYYY-MM-DD").toDate();
      const monthKey = moment(dueDate).format("MMMM yyyy");
      // Force number conversion, handle potential NaN, and convert to USD
      const profit = (Number(item.expectedProfit) || 0) * exchangeRate;

      if (!acc[monthKey]) {
        acc[monthKey] = 0;
      }
      // Ensure numeric addition
      acc[monthKey] = Number(acc[monthKey]) + profit;

      console.log(
        `Adding profit ${profit} USD (converted from ${currency}) to ${monthKey}, new total: ${acc[monthKey]}`
      );
      return acc;
    },
    {} as Record<string, number>
  );

  console.log("Calculated monthly profits:", monthlyProfits);

  // Fetch existing payments for this invoice
  const { data: existingPayments, error: existingPaymentsError } = await supabase
    .schema("nowadays")
    .from("payments")
    .select("*")
    .eq("invoice_number", invoiceNumber);

  if (existingPaymentsError) {
    console.error("Error fetching existing payments:", existingPaymentsError);
    return false;
  }

  console.log("Existing payments:", existingPayments);

  // Update or insert payments for each month
  for (const [month, profitTotal] of Object.entries(monthlyProfits)) {
    const paymentName = `Invoice EV-${invoiceNumber} - ${month} Expected Profit`;

    const existingPayment = existingPayments.find((payment) => payment.payment_name === paymentName);

    console.log(`Processing month: ${month}, profitTotal: ${profitTotal}`);
    console.log("Existing payment for this month:", existingPayment);

    // Parse the month to get the first day of the month as a timestamp
    const [monthName, year] = month.split(" ");
    const monthIndex = new Date(Date.parse(monthName + " 1, 2020")).getMonth();
    const paymentCreatedTime = Date.UTC(parseInt(year), monthIndex, 1) / 1000;

    if (profitTotal) {
      if (existingPayment) {
        // Update existing payment
        console.log(`Updating existing payment for ${month}`);
        await supabase
          .schema("nowadays")
          .from("payments")
          .update({
            profit_before_refund: Number(profitTotal), // Explicit number conversion
            payment_created_time: paymentCreatedTime,
          })
          .eq("id", existingPayment.id);
      } else {
        // Insert new payment
        console.log(`Inserting new payment for ${month}`);
        await supabase
          .schema("nowadays")
          .from("payments")
          .insert([
            {
              payment_amount_expected: 0, // Assuming no amount expected for profit-only entries
              payment_label: "invoice_generator",
              payment_name: paymentName,
              event_id: eventId,
              payment_created_time: paymentCreatedTime,
              currency: "usd",
              payment_status: StripePaymentStatus.WIRE,
              profit_before_refund: Number(profitTotal), // Explicit number conversion
              invoice_number: invoiceNumber,
            },
          ]);
      }
    } else if (existingPayment) {
      // Delete existing payment with zero profit
      console.log(`Deleting existing payment with zero profit for ${month}`);
      await supabase.schema("nowadays").from("payments").delete().eq("id", existingPayment.id);
    }
  }

  // Delete payments for months that no longer exist
  const existingPaymentNames = Object.keys(monthlyProfits).map(
    (month) => `Invoice EV-${invoiceNumber} - ${month} Expected Profit`
  );

  console.log("Existing payment names:", existingPaymentNames);

  for (const payment of existingPayments) {
    if (!existingPaymentNames.includes(payment.payment_name)) {
      console.log(`Deleting payment: ${payment.payment_name}`);
      await supabase.schema("nowadays").from("payments").delete().eq("id", payment.id);
    }
  }

  console.log("Finished processing payments for invoice:", invoiceNumber);
  return true;
};

export const addExpectedProfitToPaymentsTable = async (
  supabase: SupabaseClient,
  {
    eventId,
    paymentAmount,
    paymentLabel,
    paymentName,
    paymentDescription,
    profitTotal,
  }: {
    eventId: string;
    paymentAmount: string;
    paymentLabel: string;
    paymentName: string;
    paymentDescription: string;
    profitTotal: string;
  }
) => {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("payments")
    .insert([
      {
        payment_amount_expected: parseInt(paymentAmount) / 100,
        payment_label: paymentLabel,
        payment_name: paymentName,
        event_id: eventId,
        payment_created_time: Math.floor(Date.now() / 1000),
        currency: "usd",
        payment_status: StripePaymentStatus.WIRE,
        profit_before_refund: parseInt(profitTotal) / 100,
      },
    ])
    .select();

  console.log("DATA");
  console.log(data);
  if (error) throw error;
  return data;
};

export const getEventFinances = async (supabase: SupabaseClient, eventId: number) => {
  const { data, error } = await supabase
    .schema("nowadays")
    .from("event_finances")
    .select("*")
    .eq("event_id", eventId)
    .single();

  if (error) throw error;

  // Fetch expected profit details separately from the payments table
  const { data: expectedProfitDetails, error: expectedProfitError } = await supabase
    .schema("nowadays")
    .from("payments")
    .select("payment_name, created_at, payment_created_time, profit_before_refund, amount_refunded")
    .eq("event_id", eventId)
    .neq("payment_status", "archived")
    .gt("profit_before_refund", 0);

  if (expectedProfitError) {
    console.error("Error fetching expected profit details:", expectedProfitError);
  }

  // Fetch Stripe payment details directly from payments table with payment_created_time
  const { data: stripePaymentDetails, error: stripePaymentError } = await supabase
    .schema("nowadays")
    .from("payments")
    .select("payment_name, created_at, payment_created_time, payment_amount_captured, amount_refunded")
    .eq("event_id", eventId)
    .neq("payment_status", "archived")
    .neq("payment_status", "wire");

  if (stripePaymentError) {
    console.error("Error fetching stripe payment details:", stripePaymentError);
  }

  // Format expected profit details to match the interface
  const formattedExpectedProfitDetails = (expectedProfitDetails || []).map((detail) => ({
    payment_name: detail.payment_name || "",
    created_at: detail.created_at,
    payment_created_time: detail.payment_created_time,
    net_amount: (detail.profit_before_refund || 0) - (detail.amount_refunded || 0),
  }));

  // Format Stripe payment details to match the interface
  const formattedStripePaymentDetails = (stripePaymentDetails || []).map((detail) => ({
    payment_name: detail.payment_name || "",
    created_at: detail.created_at,
    payment_created_time: detail.payment_created_time,
    net_amount: (detail.payment_amount_captured || 0) - (detail.amount_refunded || 0),
  }));

  return {
    expectedProfit: {
      total: data.expected_profit,
      details: formattedExpectedProfitDetails,
    },
    totalStripePayments: {
      total: data.stripe_payments_total,
      details: formattedStripePaymentDetails,
    },
    totalBrexCardSpendCompleted: {
      total: data.brex_completed_total,
      details: data.brex_completed_details || [],
    },
    totalBrexCardSpendPending: {
      total: data.brex_pending_total,
      details: data.brex_pending_details || [],
    },
    totalWireSpend: {
      total: data.wire_transfers_total,
      details: data.wire_transfers_details || [],
    },
  };
};

export interface StripePaymentDetail {
  payment_name: string;
  created_at: string;
  payment_created_time?: number; // Unix timestamp
  net_amount: number;
  payment_status?: string;
}

export interface BrexExpenseDetail {
  description: string;
  date: string;
  amount: number;
  payment_status: string;
}

export interface WireDetail {
  description: string;
  date: string;
  amount: number;
}

// BREX CREDIT CARD TRANSACTIONS
export async function getCardIdForEvent(supabase: SupabaseClient, eventId: number): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .schema("nowadays")
      .from("payments_brex_customer_credit_cards")
      .select("brex_card_id")
      .eq("event_id", eventId)
      .limit(1)
      .single();

    if (error) {
      console.error("Error fetching card ID:", error);
      return null;
    }

    return data ? data.brex_card_id : null;
  } catch (error) {
    console.error("Unexpected error fetching card ID:", error);
    return null;
  }
}

export async function setBrexCardLockedStatusInSupabase(
  supabase: SupabaseClient,
  card_id: string,
  event_id: number,
  lockedStatus: boolean = true
): Promise<Response> {
  const admin_user = await getCurrentUser(supabase);
  const event = await getEvent(supabase, event_id);

  if (!admin_user) {
    return new Response("Failed to get admin user", { status: 500 });
  }

  // Fetch the current lock status of the card
  const { data: cardData, error: fetchError } = await supabase
    .schema("nowadays")
    .from("payments_brex_customer_credit_cards")
    .select("locked")
    .eq("brex_card_id", card_id)
    .single();

  if (fetchError || !cardData) {
    console.error("Error fetching current card lock status:", fetchError);
    return new Response("Failed to fetch current card lock status", {
      status: 500,
    });
  }

  // Check if the lock status needs to be updated
  if (cardData.locked !== lockedStatus) {
    console.log(`Updating card lock status for card ID ${card_id} to ${lockedStatus ? "locked" : "unlocked"}`);

    // Update the locked status in the database
    const { error: updateError } = await supabase
      .schema("nowadays")
      .from("payments_brex_customer_credit_cards")
      .update({ locked: lockedStatus })
      .eq("brex_card_id", card_id);

    if (updateError) {
      console.error("Error updating card lock status:", updateError);
      return new Response("Failed to update card lock status", { status: 500 });
    }

    // Send a Slack message about the card lock
    await sendSlackMessage(
      SlackNotificationType.BREX,
      `BREX CREDIT CARD ${lockedStatus ? "LOCKED" : "UNLOCKED"}`,
      `Admin user ${admin_user.first_name} ${admin_user.last_name} ${lockedStatus ? "locked" : "unlocked"} a Brex credit card.`,
      [
        { title: "Card ID", message: card_id },
        { title: "Event", message: event?.name ?? "Unknown" },
        { title: "Event ID", message: event?.event_id.toString() ?? "Unknown" },
      ]
    );
  }

  return new Response("Card lock status updated successfully", { status: 200 });
}

export async function updateVenueCommunicationsDuringMerge(
  supabase: SupabaseClient,
  oldHotels: StaticVenue[],
  newHotel: StaticVenue
): Promise<{ success: boolean; message?: string }> {
  try {
    // Batch update: update all old venue IDs in a single query
    const oldVenueIds = oldHotels.map((h) => h.venue_static_id);

    const { error, count } = await supabase
      .schema("nowadays")
      .from("venue_communications")
      .update({ venue_static_id: newHotel.venue_static_id })
      .in("venue_static_id", oldVenueIds);

    if (error) {
      console.log("Error updating venue_communications", error);
      return {
        success: false,
        message: "Error updating venue_communications",
      };
    }

    return {
      success: true,
      message: `Venue_communications updated successfully. Updated ${count || 0} entries.`,
    };
  } catch (error) {
    console.log("Unexpected error in updateVenueCommunicationsDuringMerge", error);
    return {
      success: false,
      message: "Unexpected error in updateVenueCommunicationsDuringMerge",
    };
  }
}
